// src/modules/purityAnalyzer/pages/MachineProfiles.jsx
// Machine registry: profile, serial, reference metal density, fluid profile.
// Reference constants (copper 8.96 etc.) live HERE, not in code.

import React, { useCallback, useEffect, useState } from 'react';
import {
  Tag, Typography, Space, Button, Modal, Form, Input, InputNumber,
  message, Popconfirm, Row, Col, Select,
} from 'antd';
import AdvancedTable from '../../../components/UI/AdvancedTable';
import {
  ToolOutlined, PlusOutlined, EditOutlined, InboxOutlined,
} from '@ant-design/icons';
import { fetchMachines, createMachine, updateMachine, archiveMachine, fetchBranchList } from '../api';
import { usePermissions } from '../../../hooks/usePermissions';

const { Title, Text } = Typography;

const STATUS_COLORS = { ACTIVE: 'green', INACTIVE: 'default', ARCHIVED: 'orange' };

export default function MachineProfiles() {
  const { can } = usePermissions();
  const canManage = can('purityAnalyzer', 'manageMachines');

  const [rows, setRows] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // null | 'new' | machine
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [machineRes, branchRes] = await Promise.all([
        fetchMachines(),
        fetchBranchList(),
      ]);
      setRows(machineRes?.data?.data || []);
      const branchData = branchRes?.data?.data?.items || branchRes?.data?.data || [];
      setBranches(branchData.map(b => ({ label: b.name, value: b._id })));
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openEdit = (machine) => {
    setEditing(machine || 'new');
    form.setFieldsValue(
      machine
        ? {
            name: machine.name,
            serialNumber: machine.serialNumber,
            model: machine.model,
            version: machine.version,
            branch: machine.branch?._id || machine.branch,
            referenceDensity: machine.referenceMetal?.density,
            fluidName: machine.immersionFluid?.name,
            fluidReferenceDensity: machine.immersionFluid?.referenceDensity,
            driftTolerance: machine.immersionFluid?.driftTolerance,
            notes: machine.notes,
          }
        : {
            referenceDensity: 8.96,
            fluidName: 'DIESEL',
            fluidReferenceDensity: 0.832,
            driftTolerance: 0.015,
          }
    );
  };

  const onSave = async () => {
    try {
      const v = await form.validateFields();
      setSaving(true);
      const body = {
        name: v.name,
        serialNumber: v.serialNumber,
        model: v.model,
        version: v.version,
        branch: v.branch || null,
        referenceMetal: { name: 'COPPER', density: v.referenceDensity },
        immersionFluid: {
          name: v.fluidName,
          referenceDensity: v.fluidReferenceDensity,
          driftTolerance: v.driftTolerance,
        },
        notes: v.notes,
      };
      if (editing === 'new') {
        await createMachine(body);
        message.success('Machine created');
      } else {
        await updateMachine(editing._id, body);
        message.success('Machine updated');
      }
      setEditing(null);
      form.resetFields();
      load();
    } catch (err) {
      if (err?.errorFields) return;
      message.error(err?.response?.data?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const onArchive = async (id) => {
    try {
      await archiveMachine(id);
      message.success('Machine archived');
      load();
    } catch (err) {
      message.error(err?.response?.data?.message || 'Archive failed');
    }
  };

  return (
    <div style={{ padding: 16 }}>
      <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 12 }} wrap>
        <Title level={3} style={{ margin: 0 }}>
          <ToolOutlined style={{ marginRight: 10 }} />Machine Profiles
        </Title>
        {canManage && (
          <Button type="primary" icon={<PlusOutlined />} onClick={() => openEdit(null)}>
            New Machine
          </Button>
        )}
      </Space>

      <AdvancedTable
        rowKey="_id" size="small" loading={loading} dataSource={rows}
        scroll={{ x: 'max-content' }}
        columns={[
          { title: 'Code', dataIndex: 'machineCode', width: 100, key: 'machineCode' },
          { title: 'Name', dataIndex: 'name', width: 150, key: 'name' },
          { title: 'Branch', dataIndex: 'branch', width: 120, key: 'branch', render: (b) => b?.name || <Text type="secondary">Unassigned</Text> },
          { title: 'Serial', dataIndex: 'serialNumber', width: 120, key: 'serialNumber', render: (v) => v || '—' },
          { title: 'Metal', dataIndex: 'metalType', width: 80, key: 'metalType' },
          {
            title: 'Reference', width: 140, key: 'reference',
            render: (_, r) => `${r.referenceMetal?.name || 'COPPER'} · ${r.referenceMetal?.density} g/cm³`,
          },
          {
            title: 'Fluid', width: 160, key: 'fluid',
            render: (_, r) => `${r.immersionFluid?.name || 'DIESEL'} · ${r.immersionFluid?.referenceDensity} g/cm³`,
          },
          {
            title: 'Active Calibration', width: 150, key: 'defaultCalibration',
            render: (_, r) => r.defaultCalibration?.calibrationNumber || <Text type="secondary">none</Text>,
          },
          {
            title: 'Status', dataIndex: 'status', width: 100, key: 'status',
            render: (v) => <Tag color={STATUS_COLORS[v]}>{v}</Tag>,
          },
          ...(canManage ? [{
            title: '', width: 160, fixed: 'right', key: 'actions',
            render: (_, r) => (
              <Space>
                <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(r)}>Edit</Button>
                {r.status !== 'ARCHIVED' && (
                  <Popconfirm title="Archive this machine?" onConfirm={() => onArchive(r._id)}>
                    <Button size="small" icon={<InboxOutlined />} />
                  </Popconfirm>
                )}
              </Space>
            ),
          }] : []),
        ]}
      />

      <Modal
        title={editing === 'new' ? 'New Machine' : `Edit ${editing?.machineCode || ''}`}
        open={!!editing} onCancel={() => setEditing(null)} onOk={onSave}
        confirmLoading={saving} okText="Save"
      >
        <Form form={form} layout="vertical">
          <Row gutter={16}>
            <Col span={16}>
              <Form.Item name="name" label="Name" rules={[{ required: true, message: 'Name is required' }]}>
                <Input placeholder="Density Analyzer 1" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="branch" label="Branch">
                <Select
                  options={branches}
                  allowClear
                  placeholder="Assign to branch..."
                  showSearch
                  filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                />
              </Form.Item>
            </Col>
          </Row>
          <Space.Compact block>
            <Form.Item name="serialNumber" label="Serial" style={{ width: '50%' }}>
              <Input />
            </Form.Item>
            <Form.Item name="model" label="Model" style={{ width: '25%' }}>
              <Input />
            </Form.Item>
            <Form.Item name="version" label="Version" style={{ width: '25%' }}>
              <Input />
            </Form.Item>
          </Space.Compact>
          <Form.Item name="referenceDensity" label="Copper Reference Density (g/cm³)"
            rules={[{ required: true, message: 'Required' }]}>
            <InputNumber style={{ width: '100%' }} min={0.1} step={0.01} precision={3} />
          </Form.Item>
          <Space.Compact block>
            <Form.Item name="fluidName" label="Fluid" style={{ width: '34%' }}>
              <Input />
            </Form.Item>
            <Form.Item name="fluidReferenceDensity" label="Nominal Density" style={{ width: '33%' }}>
              <InputNumber style={{ width: '100%' }} min={0.1} step={0.001} precision={4} />
            </Form.Item>
            <Form.Item name="driftTolerance" label="Drift Tolerance" style={{ width: '33%' }}>
              <InputNumber style={{ width: '100%' }} min={0.001} step={0.001} precision={4} />
            </Form.Item>
          </Space.Compact>
          <Form.Item name="notes" label="Notes">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
